// src/pages/SourceDocChat.js
//
// Phase 9 — SourceX "ask about visits" chatbot. Answers come from the study's
// APPROVED SOA source docs (server-side querySourceDocs), with citations — not
// from the raw protocol PDF. Distinct from the global eligibility ChatLauncher
// (chatWithAI).
//
// It used to be a full-width card sitting between the three build steps and the
// patient list — the widest thing on the page, for a feature nobody is using
// yet. It is now one button in the header's "Advanced" row, beside the other
// two parked entry points, opening its panel on demand. Same component, same
// query, same citations; only where it lives has changed.

import { useCallback, useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, ChevronDown, FileText, X } from "lucide-react";
import { askSourceDocs } from "../services/protocolExtractionService";

const SUGGESTIONS = [
  "What happens at Visit 1?",
  "When is ECG required?",
  "Which visits are phone visits?",
];

export default function SourceDocChat({ studyId }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // Escape, and a click anywhere outside. A panel that hangs over the page
  // until its own button is found again is the usual complaint about anchored
  // dropdowns; the conversation itself is kept, so reopening resumes it.
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) close();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, close]);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const r = await askSourceDocs(studyId, q);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: r.answer || "(no answer)", citations: r.citations || [] },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `Error: ${e.message || "query failed"}`, citations: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      {/* Sized and coloured to match the two Advanced buttons beside it, and
          no icon, for the same reason they lost theirs. The chevron stays: it
          is the one thing that says this button opens a panel where its
          neighbours navigate away. */}
      <button
        onClick={() => setOpen((o) => !o)}
        title="Ask about this study's visits — answered from its approved SOA source documents (not part of the current workflow)"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] transition ${
          open
            ? "bg-white/[0.09] border-white/25 text-white"
            : "bg-white/[0.03] border-white/10 hover:bg-white/[0.07] text-slate-400 hover:text-slate-200"
        }`}
      >
        Ask about Visits (AI)
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            // Solid, not translucent: the page scrolls behind this and a
            // see-through panel made the answers unreadable. z-50 clears the
            // header and the study picker below it.
            className="absolute right-0 z-50 mt-1.5 w-[26rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-white/15 bg-[#0c0826] shadow-[0_24px_70px_-15px_rgba(0,0,0,0.85)] overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
              <span className="min-w-0 flex-1 text-[11px] text-slate-400">
                Answered by AI from this study's approved SOA source docs. AI can make mistakes: use answers as a helper, not the final word.
              </span>
              <button
                onClick={close}
                aria-label="Close"
                className="shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div ref={scrollRef} className="max-h-72 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && (
                <div className="text-xs text-slate-400">
                  Ask about a visit, assessment, or week. Try:
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => ask(s)}
                        className="px-2 py-1 rounded-md bg-white/[0.05] border border-white/10 hover:bg-white/[0.1] text-[11px] text-slate-200 transition"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                  <div
                    className={`inline-block max-w-[85%] text-left rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-purple-600/30 border border-purple-400/30 text-white"
                        : "bg-white/[0.05] border border-white/10 text-slate-100"
                    }`}
                  >
                    {m.content}
                    {m.role === "assistant" && m.citations?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5 border-t border-white/10 pt-2">
                        {m.citations.map((c, j) => (
                          <span
                            key={j}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10 text-[10px] text-slate-300"
                          >
                            <FileText className="w-2.5 h-2.5" />
                            {c.visit_label || c.visit_id}
                            {c.week ? ` · Wk ${c.week}` : ""}
                            {c.source_pages?.length ? ` · p.${c.source_pages.join(",")}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="text-left">
                  <div className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm bg-white/[0.05] border border-white/10 text-slate-300">
                    <Loader2 className="w-4 h-4 animate-spin" /> Searching approved source docs…
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 px-3 py-2 border-t border-white/5">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask()}
                placeholder="Ask about this study's visits…"
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-emerald-400/40"
              />
              <button
                onClick={() => ask()}
                disabled={loading || !input.trim()}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white disabled:opacity-40 transition"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
